// Floating "Ask AI" launcher. Position + visibility are per-user preferences
// stored in the backend (users.floating_button_visible / floating_button_position),
// so they sync across devices. Read via useMe(); update via the
// /me/floating-button endpoint.
//
// The bubble itself opens the chat on click. A small "settings" cog
// adjacent to it lets the user move the bubble between six fixed slots or
// hide it. Once hidden it can ONLY be re-enabled from Settings, by design.
import { useEffect, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Bot, X, Settings as SettingsIcon, EyeOff, Check } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useSetMyFloatingButton,
  type FloatingButtonPosition,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useMe, useMyFeatures } from "@/lib/auth";
import { AskAiPanel } from "./ask-ai-panel";

const POSITION_LABELS: Record<FloatingButtonPosition, string> = {
  "bottom-right": "Bottom right",
  "bottom-left": "Bottom left",
  "top-right": "Top right",
  "top-left": "Top left",
  "center-right": "Middle right",
  "center-left": "Middle left",
};

const POSITION_ORDER: FloatingButtonPosition[] = [
  "center-right",
  "center-left",
  "top-right",
  "top-left",
  "bottom-right",
  "bottom-left",
];

// Map a position slot to an inline style so the placement is bulletproof
// (no Tailwind purge or specificity gotchas can knock the button off-screen).
function styleForPosition(
  pos: FloatingButtonPosition,
): CSSProperties {
  const base: CSSProperties = {
    position: "fixed",
    zIndex: 9999,
    transition: "top 200ms ease, bottom 200ms ease, left 200ms ease, right 200ms ease",
  };
  const EDGE = "16px";
  const TOP_OFFSET = "80px"; // clear the app header
  const BOTTOM_OFFSET = "16px";

  switch (pos) {
    case "bottom-right":
      return { ...base, right: EDGE, bottom: BOTTOM_OFFSET };
    case "bottom-left":
      return { ...base, left: EDGE, bottom: BOTTOM_OFFSET };
    case "top-right":
      return { ...base, right: EDGE, top: TOP_OFFSET };
    case "top-left":
      return { ...base, left: EDGE, top: TOP_OFFSET };
    case "center-left":
      return { ...base, left: EDGE, top: "50%", transform: "translateY(-50%)" };
    case "center-right":
    default:
      return { ...base, right: EDGE, top: "50%", transform: "translateY(-50%)" };
  }
}

// The cog button sits just above the bubble for top-anchored slots, just
// below for bottom-anchored, and slightly above for centred slots — so it
// never overflows the viewport.
function cogStyleForPosition(
  pos: FloatingButtonPosition,
  bubbleStyle: CSSProperties,
): CSSProperties {
  const COG_OFFSET = "56px";
  const style: CSSProperties = {
    position: "fixed",
    zIndex: 9999,
    transition: bubbleStyle.transition,
  };
  if (bubbleStyle.right !== undefined) style.right = bubbleStyle.right;
  if (bubbleStyle.left !== undefined) style.left = bubbleStyle.left;

  if (pos.startsWith("bottom")) {
    style.bottom = `calc(${bubbleStyle.bottom as string} + ${COG_OFFSET})`;
  } else if (pos.startsWith("top")) {
    style.top = `calc(${bubbleStyle.top as string} + ${COG_OFFSET})`;
  } else {
    // centre: cog goes just above the bubble
    style.top = `calc(50% - ${COG_OFFSET})`;
    style.transform = "translateY(-50%)";
  }
  return style;
}

// Tailwind's `md` breakpoint is 768px — match that here so the JS-side
// device check stays in lock-step with the rest of the app's responsive CSS.
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    // Initial sync in case SSR-ish initial value drifted.
    setIsMobile(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

export function AskAiBubble() {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const isMobile = useIsMobile();
  const { me } = useMe();
  const { hasFeature } = useMyFeatures();
  const qc = useQueryClient();
  const updatePrefs = useSetMyFloatingButton({
    mutation: {
      onSuccess: () => {
        // Bust the cached /me so position/visibility re-render immediately.
        qc.invalidateQueries({ queryKey: ["/api/me"] });
      },
    },
  });

  // Portals can only mount on the client. Defer until after first render
  // to keep us SSR-safe and avoid hydration warnings.
  useEffect(() => setMounted(true), []);

  if (!mounted || !me) return null;

  // MetalUI AI Assistant (Task #927) — the bubble only exists when the
  // platform-owner-only `ai_assistant_enabled` flag is on for this tenant.
  // The backend gates `/ask-ai` with the same flag, so hiding here keeps FE
  // and BE in lockstep (no dead launcher that 403s on click).
  if (!hasFeature("ai_assistant_enabled")) return null;

  // Visibility is per device-class. On mobile (<768px) we honour the mobile
  // flag; on desktop the existing one. Either can hide the button
  // independently. Re-enable in Settings → Interface Preferences.
  const visibleHere = isMobile
    ? me.floatingButtonVisibleMobile !== false
    : me.floatingButtonVisible !== false;
  if (!visibleHere) return null;

  const position = (me.floatingButtonPosition ??
    "center-right") as FloatingButtonPosition;
  // Mobile controls occupy real space in the sticky shell header. A fixed
  // corner (even above the dock) would still cover cards as they scroll.
  const bubbleStyle = isMobile ? undefined : styleForPosition(position);
  const cogStyle = isMobile ? undefined : cogStyleForPosition(position, bubbleStyle!);

  function setPosition(next: FloatingButtonPosition) {
    if (next === position) return;
    updatePrefs.mutate({ data: { floatingButtonPosition: next } });
  }

  function hide() {
    // Hide only the device-class the user is currently on, so hiding on
    // mobile doesn't also hide the button on their laptop.
    updatePrefs.mutate({
      data: isMobile
        ? { floatingButtonVisibleMobile: false }
        : { floatingButtonVisible: false },
    });
    setOpen(false);
  }

  const controls = (
    <>
      <Button
        type="button"
        size="icon"
        aria-label={open ? "Close Ask AI" : "Open Ask AI"}
        onClick={() => setOpen((o) => !o)}
        data-testid="ask-ai-bubble"
        style={bubbleStyle}
        className={cn(
          "rounded-full shadow-lg",
          "h-12 w-12 md:h-14 md:w-14 lg:h-16 lg:w-16",
          "bg-primary text-primary-foreground hover:bg-primary/90",
        )}
      >
        {open ? (
          <X className="h-5 w-5 md:h-6 md:w-6 lg:h-7 lg:w-7" />
        ) : (
          <Bot className="h-5 w-5 md:h-6 md:w-6 lg:h-7 lg:w-7" />
        )}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="icon"
            variant="secondary"
            aria-label="Ask AI button options"
            data-testid="ask-ai-bubble-options"
            style={cogStyle}
            className="h-11 w-11 md:h-8 md:w-8 rounded-full shadow-md border"
          >
            <SettingsIcon className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={position.endsWith("-left") ? "start" : "end"}
          className="w-52"
          style={{ zIndex: 10000 }}
        >
          <DropdownMenuLabel>{isMobile ? "Desktop button position" : "Move button"}</DropdownMenuLabel>
          {POSITION_ORDER.map((p) => (
            <DropdownMenuItem
              key={p}
              onSelect={() => setPosition(p)}
              data-testid={`ask-ai-position-${p}`}
              className="flex items-center justify-between"
            >
              <span>{POSITION_LABELS[p]}</span>
              {p === position ? <Check className="h-4 w-4" /> : null}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={hide}
            data-testid="ask-ai-hide"
            className="text-destructive focus:text-destructive"
          >
            <EyeOff className="h-4 w-4 mr-2" />
            Hide button
          </DropdownMenuItem>
          <DropdownMenuLabel className="text-[11px] font-normal text-muted-foreground pt-1">
            Re-enable from Settings.
          </DropdownMenuLabel>
        </DropdownMenuContent>
      </DropdownMenu>

    </>
  );

  const panel = <AskAiPanel open={open} onClose={() => setOpen(false)} />;
  if (isMobile) {
    return (
      <>
        <div
          role="group"
          aria-label="Assistant tools"
          data-testid="mobile-assistant-tools"
          className="flex shrink-0 items-center justify-end gap-3 border-t border-border/50 bg-background py-1.5 pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))]"
        >
          {controls}
        </div>
        {createPortal(panel, document.body)}
      </>
    );
  }

  return createPortal(<>{controls}{panel}</>, document.body);
}
